import { api } from './api'
import { getAll } from './pagination'

export interface NotificationTemplate {
  id: number
  name: string
  template_type: string
  channel: 'sms' | 'email' | 'both'
  subject: string
  content: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface Notification {
  id: number
  template?: number
  recipient: number
  recipient_name: string
  channel: 'sms' | 'email'
  subject: string
  content: string
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'read'
  sent_at?: string
  delivered_at?: string
  read_at?: string
  provider_message_id?: string
  failure_reason?: string
  context_data: Record<string, any>
  created_at: string
  updated_at: string
}

export interface NotificationStats {
  period: {
    start_date: string
    end_date: string
    days: number
  }
  total_notifications: number
  template_breakdown: Record<string, number>
  /** Every status with its count. Older servers left it out, so it is optional. */
  status_breakdown?: Record<string, number>
  sent_notifications?: number
  delivered_notifications?: number
  failed_notifications?: number
  recent_notifications: Notification[]
}

export interface AttendanceNotificationStats {
  period: {
    start_date: string
    end_date: string
    days: number
  }
  total_notifications: number
  template_breakdown: Record<string, number>
  status_breakdown: Record<string, number>
  recent_notifications: Notification[]
}

export interface ProcessAttendanceNotificationsResponse {
  message: string
  stats: {
    absent_notifications: number
    late_notifications: number
    poor_attendance_alerts: number
    errors: number
  }
}

export interface WeeklySummaryResponse {
  message: string
  sent_count: number
}

class NotificationService {
  // Templates
  async getTemplates(params?: {
    template_type?: string
    channel?: string
    is_active?: boolean
  }) {
    return getAll<NotificationTemplate>('/notifications/templates/', params)
  }

  async getTemplate(id: number) {
    const response = await api.get(`/notifications/templates/${id}/`)
    return response.data
  }

  async createTemplate(data: Partial<NotificationTemplate>) {
    const response = await api.post('/notifications/templates/', data)
    return response.data
  }

  async updateTemplate(id: number, data: Partial<NotificationTemplate>) {
    const response = await api.patch(`/notifications/templates/${id}/`, data)
    return response.data
  }

  async deleteTemplate(id: number) {
    const response = await api.delete(`/notifications/templates/${id}/`)
    return response.data
  }

  // Notifications
  async getNotifications(params?: {
    recipient?: number
    channel?: string
    status?: string
    template_type?: string
    page?: number
    limit?: number
  }) {
    return getAll<Notification>('/notifications/notifications/', params)
  }

  async getNotification(id: number) {
    const response = await api.get(`/notifications/notifications/${id}/`)
    return response.data
  }

  async markAsRead(id: number) {
    const response = await api.patch(`/notifications/notifications/${id}/`, {
      status: 'read',
      read_at: new Date().toISOString()
    })
    return response.data
  }

  // Test notifications
  async sendTestNotification(data: {
    recipient_id: number
    channel: 'sms' | 'email'
    template_id?: number
    content?: string
    subject?: string
  }) {
    const response = await api.post('/notifications/test/', data)
    return response.data
  }

  async sendBulkNotification(data: {
    recipient_ids: number[]
    channel: 'sms' | 'email'
    template_id?: number
    content?: string
    subject?: string
  }) {
    const response = await api.post('/notifications/bulk/', data)
    return response.data
  }

  // Specific notification types
  async sendFeeReminder(studentId: number) {
    const response = await api.post(`/notifications/fee-reminder/${studentId}/`)
    return response.data
  }

  async sendPaymentNotification(paymentId: number) {
    const response = await api.post(`/notifications/payment/${paymentId}/`)
    return response.data
  }

  async sendPRNNotification(prnId: number) {
    const response = await api.post(`/notifications/prn/${prnId}/`)
    return response.data
  }

  // Statistics
  async getNotificationStats(days: number = 30): Promise<NotificationStats> {
    const response = await api.get('/notifications/statistics/', {
      params: { days }
    })
    return response.data
  }

  // Attendance notifications
  async processAttendanceNotifications(): Promise<ProcessAttendanceNotificationsResponse> {
    const response = await api.post('/notifications/attendance/process/')
    return response.data
  }

  async sendWeeklyAttendanceSummaries(): Promise<WeeklySummaryResponse> {
    const response = await api.post('/notifications/attendance/weekly-summaries/')
    return response.data
  }

  async getAttendanceNotificationStats(days: number = 30): Promise<AttendanceNotificationStats> {
    const response = await api.get('/notifications/attendance/statistics/', {
      params: { days }
    })
    return response.data
  }

  async createAttendanceTemplates() {
    const response = await api.post('/notifications/attendance/templates/')
    return response.data
  }

  // Preferences
  async getPreferences() {
    const response = await api.get('/notifications/preferences/')
    return response.data
  }

  async updatePreferences(data: {
    sms_enabled: boolean
    email_enabled: boolean
    attendance_notifications: boolean
    payment_notifications: boolean
    general_notifications: boolean
  }) {
    const response = await api.patch('/notifications/preferences/', data)
    return response.data
  }
}

export const notificationService = new NotificationService()
